import { zoneMapQuerySchema, type ZoneListQuery, type ZoneMapQuery } from '@chukua/common';
import { useEffect, useMemo, useState } from 'react';
import { parseListParams, toListParams } from '../../../../../shared/policies/list-params';
import { listZonesForMap, type Zone } from '../../../zones.api';

/** `queryKey` is the search and filters the loaded zones answer — the map reframes when it changes, not on a reload. */
export type ZonesForMapState = { status: 'loading' } | { status: 'loaded'; zones: Zone[]; queryKey: string } | { status: 'error' };

function mapQueryOf({ q, status, kind }: ZoneListQuery): ZoneMapQuery {
  return { q, status, kind };
}

/**
 * Every zone the list's search and filters match, with geometry, for the map. Keyed on those alone,
 * so paging or sorting the table doesn't refetch or reframe the map.
 */
export function useZonesForMap(listQuery: ZoneListQuery) {
  const key = toListParams(mapQueryOf(listQuery)).toString();
  const query = useMemo(() => parseListParams(zoneMapQuerySchema, new URLSearchParams(key)), [key]);
  const [state, setState] = useState<ZonesForMapState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listZonesForMap(query)
      .then((zones) => {
        if (!cancelled) setState({ status: 'loaded', zones, queryKey: key });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [query, key]);

  /** Re-fetches after a mutation commits, so a deactivated zone turns grey. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', zones: await listZonesForMap(query), queryKey: key });
    } catch {
      setState({ status: 'error' });
    }
  }

  return { state, reload };
}
